import { takeLatest, select, put, call } from 'redux-saga/effects'
import _ from 'lodash'
import toast from 'react-hot-toast'

import api from 'Src/api'

import {
  SET_FLOW_HISTORY,
  updateFlowHistory,
  INIT_FLOW_PAGE,
  SAVE_FLOW,
  setActiveFlow,
  SET_ACTIVE_FLOW,
  fetchFlow,
  FETCH_FLOW,
  fetchFlowDone,
  SAVE_AND_PUBLISH_FLOW,
  ADD_ACTION,
  UPDATE_ACTION,
  CREATE_STEP,
  DELETE_ACTION,
  saveFlowSuccess,
  saveandPublishFlowSuccess,
  DELETE_FLOW,
  UPDATE_STEP_NAME,
  DELETE_STEP,
  UPDATE_FLOW_PROPERTIES,
  showUnsaveMessage,
  hideUnsaveMessage
} from 'Actions/flow'
import { getFlowHistory, getActiveFlow } from 'Selectors/flow'
import { fetchFlowsSuccess, FETCH_FLOWS } from 'Actions/bots'

const LIST_FLOWS_URL = 'v1/bot-profiles/:botId/bot-flows'
const FLOW_URL = 'v1/bot-profiles/:botId/bot-flows/:id'
const PUBLISH_FLOW_URL = 'v1/bot-flows/:id/publish'

export default function* appSaga() {
  yield takeLatest(SET_FLOW_HISTORY, onSetHistory)
  yield takeLatest(INIT_FLOW_PAGE, onInitFlowPage)
  yield takeLatest(UPDATE_FLOW_PROPERTIES, onUpdateFlowProperties)
  yield takeLatest(SAVE_FLOW, onSaveFlow)
  yield takeLatest(SAVE_AND_PUBLISH_FLOW, onSaveandPublishFlow)
  yield takeLatest(DELETE_FLOW, onDeleteFlow)

  yield takeLatest(SET_ACTIVE_FLOW, onSetActiveFlow)
  yield takeLatest(FETCH_FLOW, onFetchFlow)
  yield takeLatest(FETCH_FLOWS, onFetchFlows)

  yield takeLatest(ADD_ACTION, addUnloadEvent)
  yield takeLatest(UPDATE_ACTION, addUnloadEvent)
  yield takeLatest(DELETE_ACTION, addUnloadEvent)
  yield takeLatest(CREATE_STEP, addUnloadEvent)
  yield takeLatest(UPDATE_STEP_NAME, addUnloadEvent)
  yield takeLatest(DELETE_STEP, addUnloadEvent)
}

function* onSetHistory(action) {
  const payload = _.get(action, 'payload', {})

  const { goToStep, row } = payload

  if (!goToStep || row === undefined) {
    return
  }

  const flowHistory = yield select(getFlowHistory)
  if (!flowHistory) {
    return
  }

  const updatedFlowHistory = flowHistory.split('.').slice(0, row)
  updatedFlowHistory.push(goToStep)
  const newHistory = updatedFlowHistory.join('.')

  if (flowHistory === newHistory) {
    return
  }

  yield put(updateFlowHistory(newHistory))
}

function* onInitFlowPage(action) {
  const { botId, flowId } = action.payload
  const flows = yield call(fetchFlows, botId)
  yield put(fetchFlowsSuccess(botId, flows))
  yield put(
    setActiveFlow({
      botId,
      flowId
    })
  )
}

function* paginate(url, urlParams) {
  const results = []
  const meta = {}

  let offset = 0
  const limit = 15

  let lastPageFetched = false
  do {
    const { data } = yield call(api.get, url, {
      params: {
        offset,
        limit
      },
      urlParams
    })

    results.push(...data.results)
    Object.assign(meta, data.meta)

    offset = offset + limit

    if (results.length >= data.meta.total) {
      lastPageFetched = true
    }
  } while (!lastPageFetched)

  return {
    results,
    meta
  }
}

function* onFetchFlows(action) {
  const { botId } = action.payload
  const flows = yield call(fetchFlows, botId)
  yield put(fetchFlowsSuccess(botId, flows))
}

function* fetchFlows(botId) {
  try {
    const data = yield call(paginate, LIST_FLOWS_URL, { botId })
    return data.results
  } catch (err) {
    console.error('Error while fetching flows', err)
  }
}

function* onSetActiveFlow(action) {
  const { flowId, botId } = action.payload
  yield put(fetchFlow({ flowId, botId }))
}

function* onFetchFlow(action) {
  const { flowId, botId } = action.payload

  try {
    const response = yield call(api.get, FLOW_URL, { urlParams: { id: flowId, botId } })
    const flow = response.data
    yield put(fetchFlowDone(flow))
    yield put(updateFlowHistory(flow.conversation.starting_step_id))
  } catch (err) {
    console.error('Error while fetching flows', err)
  }
}

function* onSaveFlow(action) {
  const { flowId, botId, showToast = false } = action.payload
  const flow = yield select(getActiveFlow)

  try {
    const { data } = yield call(
      api.patch,
      FLOW_URL,
      { flow },
      {
        urlParams: {
          id: flowId,
          botId
        }
      }
    )
    yield put(saveFlowSuccess({ flowId }))
    yield call(removeUnloadEvent)

    // If BE updates/creates a non existent property
    if (data.internalNameCreated) {
      yield put(fetchFlow(flowId))
    }
    if (!showToast) {
      toast.success('Bot saved successfully')
    }
  } catch (err) {
    console.error('Error while updating flow', err)
    toast.error('Error while updating flow')
  }
}

function* onUpdateFlowProperties(action) {
  const { flowId, botId, name } = action.payload

  const data = {
    name
  }

  const payload = _.pickBy(data, _.identity)

  if (_.isEmpty(payload)) {
    return
  }

  try {
    yield call(api.patch, FLOW_URL, data, {
      urlParams: {
        id: flowId,
        botId
      }
    })

    toast.success('Bot saved successfully')
  } catch (err) {
    console.error('Error while updating flow properties', err)
    toast.error('Error while updating flow')
  }
}

function* onSaveandPublishFlow(action) {
  const { flowId, botId } = action.payload

  try {
    yield call(onSaveFlow, { payload: { flowId, botId, showToast: true } })
    const { data } = yield call(api.post, PUBLISH_FLOW_URL, {}, { urlParams: { id: flowId } })
    const latestPublishedRevision = data?.flow?.latestPublishedRevision
    yield put(saveandPublishFlowSuccess({ flowId, latestPublishedRevision }))
    toast.success('Bot saved and published successfully')
  } catch (err) {
    console.error('Error while publishing flow', err)
    toast.error('Error while publishing flow')
  }
}

function* onDeleteFlow(action) {
  const { flowId, botId } = action.payload

  try {
    yield call(api.delete, FLOW_URL, { urlParams: { id: flowId, botId } })
    toast.success('Bot deleted successfully...')
    yield call(onInitFlowPage, { payload: { botId } })
  } catch (err) {
    console.error('Error while deleting flow', err)
    toast.error('Error while deleting flow')
  }
}

const unloadEventHandler = (e) => {
  e.preventDefault()
  e.returnValue = 'Your changes will be lost'
}

const addUnloadEvent = function* () {
  yield put(showUnsaveMessage({ isUnsaved: true }))
  window.addEventListener('beforeunload', unloadEventHandler)
}

const removeUnloadEvent = function* () {
  yield put(hideUnsaveMessage({ isUnsaved: false }))
  window.removeEventListener('beforeunload', unloadEventHandler)
}
